// 학생 #/students · 상세 #/students/<id> (v0.3 Students.kt · SCREENS §1-6·7)
// compact 카드 1단 · medium 카드 2열(누르면 상세 주소) · expanded 목록 360 | 상세 · large 목록 표 | 상세
import { useState, type CSSProperties } from "react";
import { deleteItem, deleteWarn, recreate, toggleItem } from "../data/actions";
import { openItems, sessionOrder, statusVar, upcoming, warnsOf } from "../data/select";
import { useView, type View } from "../data/store";
import { St, WARN_TYPES, type Student } from "../data/types";
import { DOW, fmtDate, todayStr } from "../lib/date";
import { go, useWidthClass, type Route } from "../ui/hooks";
import { Icon } from "../ui/icons";
import { Empty, Pill, Section, StatTile, StatusPill, StudentName, toast } from "../ui/parts";
import { Loading } from "./Dashboard";
import { AddItemsBox, openSheet } from "./sheets";

export function Students({ route }: { route: Route }) {
  const v = useView();
  const wc = useWidthClass();
  const [q, setQ] = useState("");
  const [f, setF] = useState("전체");
  const [school, setSchool] = useState("전체 학교");
  if (!v.status.loaded) return <Loading />;
  const th = v.settings.warnThreshold;
  const selId = route.param;
  const sel = selId ? v.students.find((s) => s.id === selId) : undefined;
  const split = wc === "expanded" || wc === "large";

  // compact·medium 에서 상세 주소로 들어오면 상세만
  if (selId && !split) return <div className="page"><DetailHead back /><StudentDetail v={v} st={sel} wide={wc === "medium"} /></div>;

  const schools = ["전체 학교", ...[...new Set(v.students.map((s) => s.school).filter(Boolean))].sort()];
  const filters = ["전체", "남은 클리닉", "일정 없음", `경고 ${th}장+`];
  const list = v.students.filter((s) =>
    (!q.trim() || s.name.includes(q.trim()) || s.school.includes(q.trim())) &&
    (school === "전체 학교" || s.school === school) &&
    (f === "남은 클리닉" ? openItems(v, s.id).length > 0
      : f === "일정 없음" ? openItems(v, s.id).length > 0 && upcoming(v, s.id).length === 0
        : f.startsWith("경고") ? warnsOf(v, s.id).length >= th : true));

  const toolbar = (
    <>
      <div className="toolbar">
        <label className="search grow">
          <Icon name="search" size={20} />
          <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="학생 이름·학교 검색" aria-label="학생 검색" />
        </label>
        <button type="button" className="btn" onClick={() => openSheet({ kind: "student", id: null })}><Icon name="plus" size={18} /> 학생</button>
      </div>
      <div className="chips filters" role="group" aria-label="거르기">
        {filters.map((x) => <button key={x} type="button" className={"chip" + (f === x ? " on" : "")} aria-pressed={f === x} onClick={() => setF(x)}>{x}</button>)}
        <select className="input input-inline" value={school} onChange={(e) => setSchool(e.target.value)} aria-label="학교">
          {schools.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <p className="muted small lead">{list.length}명</p>
    </>
  );

  const open = (id: string) => go(`students/${encodeURIComponent(id)}`);

  if (!split) {
    return (
      <div className="page">
        {toolbar}
        {list.length === 0 ? <Empty>조건에 맞는 학생이 없습니다.</Empty> : (
          <div className={"card-grid " + (wc === "medium" ? "cg-medium" : "")}>
            {list.map((s) => <StudentCard key={s.id} v={v} s={s} onOpen={() => open(s.id)} />)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={"page split split-" + wc}>
      <div className="split-list">
        {toolbar}
        {list.length === 0 ? <Empty>조건에 맞는 학생이 없습니다.</Empty> : wc === "large" ? (
          <div className="table-wrap" data-scroll-x>
            <table className="tbl tbl-click">
              <thead><tr><th>이름</th><th>학교</th><th>요일</th><th>남은</th><th>경고</th><th>다음 일정</th></tr></thead>
              <tbody>
                {list.map((s) => {
                  const wn = warnsOf(v, s.id).length;
                  const up = upcoming(v, s.id)[0];
                  const on = s.id === selId;
                  return (
                    <tr key={s.id} className={on ? "on" : ""} onClick={() => open(s.id)}>
                      <td><a className="cell-link" href={`#/students/${encodeURIComponent(s.id)}`} aria-current={on ? "page" : undefined}><StudentName st={s} school={false} /></a></td>
                      <td className="muted nowrap">{s.school}</td>
                      <td className="small nowrap">{[...s.slots].sort((a, b) => a.dow - b.dow).map((x) => DOW[x.dow - 1]).join("·") || "-"}</td>
                      <td className="num">{openItems(v, s.id).length || "✓"}</td>
                      <td className="num">{wn ? <Pill color={wn >= th ? "var(--color-danger)" : "var(--color-late)"}>{wn}</Pill> : "0"}</td>
                      <td className="small nowrap">{up ? `${fmtDate(up.date)} ${up.time}` : openItems(v, s.id).length ? <span className="warn-text">없음</span> : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <ul className="list">
            {list.map((s) => (
              <li key={s.id}>
                <a className={"row-card" + (s.id === selId ? " on" : "")} href={`#/students/${encodeURIComponent(s.id)}`} aria-current={s.id === selId ? "page" : undefined}>
                  <StudentName st={s} />
                  <span className="grow" />
                  {warnsOf(v, s.id).length ? <Pill color={warnsOf(v, s.id).length >= th ? "var(--color-danger)" : "var(--color-late)"}>경고 {warnsOf(v, s.id).length}</Pill> : null}
                  <Pill color={openItems(v, s.id).length ? "var(--color-accent)" : "var(--color-success)"}>{openItems(v, s.id).length ? `남은 ${openItems(v, s.id).length}` : "없음"}</Pill>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="split-detail">
        {sel ? <StudentDetail v={v} st={sel} wide={wc === "large"} /> : <Empty>왼쪽에서 학생을 고르면 상세가 여기에 열립니다.</Empty>}
      </div>
    </div>
  );
}

function DetailHead({ back }: { back?: boolean }) {
  return back ? <a className="link back" href="#/students"><Icon name="left" size={18} /> 학생 목록</a> : null;
}

function StudentCard({ v, s, onOpen }: { v: View; s: Student; onOpen: () => void }) {
  const th = v.settings.warnThreshold;
  const open = openItems(v, s.id).length;
  const wn = warnsOf(v, s.id).length;
  const up = upcoming(v, s.id)[0];
  return (
    <button type="button" className="row-card scard-btn" onClick={onOpen}>
      <span className="scard-btn-top">
        <StudentName st={s} />
        <span className="grow" />
        {wn ? <Pill color={wn >= th ? "var(--color-danger)" : "var(--color-late)"}>경고 {wn}</Pill> : null}
        <Pill color={open ? "var(--color-accent)" : "var(--color-success)"}>{open ? `남은 ${open}` : "없음"}</Pill>
      </span>
      <span className={"small " + (!up && open ? "warn-text" : "muted")}>다음 일정: {up ? `${fmtDate(up.date)} ${up.time}` : open ? "없음 — 잡아야 함" : "—"}</span>
    </button>
  );
}

/** 학생 상세 — 머리 · 숫자 4칸 · 남은 클리닉 · 다가오는 일정 · 경고카드 · 과거 이력 */
export function StudentDetail({ v, st, wide }: { v: View; st?: Student; wide: boolean }) {
  const [adding, setAdding] = useState(false);
  if (!st) return <Empty>없거나 삭제된 학생입니다.</Empty>;
  const th = v.settings.warnThreshold;
  const open = openItems(v, st.id).sort((a, b) => a.week.localeCompare(b.week, "ko"));
  const ws = warnsOf(v, st.id).sort((a, b) => b.date.localeCompare(a.date));
  const sessions = v.sessions.filter((s) => s.studentId === st.id).sort(sessionOrder).reverse();
  const past = sessions.filter((s) => s.status !== St.PLAN || s.date < todayStr());
  const up = upcoming(v, st.id);
  const closed = past.filter((s) => s.status !== St.PLAN);
  const rate = closed.length ? `${Math.round((closed.filter((s) => s.status !== St.NOSHOW).length * 100) / closed.length)}%` : "—";

  const itemsSec = (
    <Section title="남은 클리닉" count={open.length} action={<button type="button" className="btn btn-ghost" onClick={() => setAdding(!adding)}>{adding ? "접기" : "＋ 시험지 추가"}</button>}>
      {adding ? <AddItemsBox studentId={st.id} onDone={() => setAdding(false)} /> : null}
      {open.length === 0 ? <Empty>남은 시험지가 없습니다.</Empty> : (
        <ul className="list">
          {open.map((it) => (
            <li key={it.id} className="item-row">
              <span className="check-week">[{it.week}]</span>
              <span className="grow item-title">{it.title}<span className="muted small"> · {it.createdBy}</span></span>
              <button type="button" className="btn btn-ghost" onClick={() => { toggleItem(it, null); toast("통과 처리했습니다", { undo: () => toggleItem({ ...it, done: true }, null) }); }}>통과</button>
              <button type="button" className="btn btn-ghost btn-danger-text" onClick={() => { deleteItem(it.id); toast("삭제했습니다", { undo: () => recreate("items", it) }); }}>삭제</button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
  const upSec = (
    <Section title="다가오는 일정" count={up.length} action={<a className="link" href={`#/register?student=${encodeURIComponent(st.id)}`}>＋ 일정 등록</a>}>
      {up.length === 0 ? <Empty>{open.length ? "남은 클리닉이 있는데 일정이 없습니다." : "없음"}</Empty> : (
        <ul className="list">{up.map((u) => (
          <li key={u.id}><button type="button" className="row-card" onClick={() => openSheet({ kind: "session", id: u.id })}>
            <span className="time">{fmtDate(u.date)} {u.time}</span><span className="grow" /><span className="muted small">등록 {u.createdBy}</span>
          </button></li>
        ))}</ul>
      )}
    </Section>
  );
  const warnSec = (
    <Section title="경고카드" count={ws.length} warn={ws.length >= th} action={<button type="button" className="btn btn-ghost" onClick={() => openSheet({ kind: "warn", studentId: st.id })}>＋ 경고카드</button>}>
      <div className="chips">
        {WARN_TYPES.map((t) => { const n = ws.filter((w) => w.type === t).length; return <Pill key={t} color={n ? "var(--color-late)" : "var(--color-text-muted)"}>{t} {n}</Pill>; })}
      </div>
      <ul className="list mt">
        {ws.slice(0, 12).map((w) => (
          <li key={w.id} className="item-row">
            <span className="nowrap small">{fmtDate(w.date)}</span>
            <span className="grow"><b>{w.type}</b> <span className="muted small">{w.note}</span></span>
            <span className="muted small nowrap">{w.createdBy}{w.sessionId ? " · 자동" : ""}</span>
            <button type="button" className="btn btn-ghost btn-danger-text" onClick={() => { deleteWarn(w.id); toast("경고카드를 지웠습니다", { undo: () => recreate("warns", w) }); }}>지우기</button>
          </li>
        ))}
      </ul>
    </Section>
  );
  const pastSec = (
    <Section title="과거 이력" count={past.length}>
      {past.length === 0 ? <Empty>아직 기록이 없습니다.</Empty> : (
        <ul className="list">
          {past.slice(0, 20).map((p) => {
            const passed = v.items.filter((i) => i.doneSession === p.id);
            return (
              <li key={p.id}>
                <button type="button" className="row-card hist" style={{ "--accent": statusVar(p.status) } as CSSProperties} onClick={() => openSheet({ kind: "session", id: p.id })}>
                  <span className="hist-top">
                    <span className="time">{fmtDate(p.date)} {p.time}</span>
                    {p.status === St.PLAN ? <Pill color="var(--color-text-muted)">확인 안 됨</Pill> : <StatusPill status={p.status} />}
                    {p.late ? <Pill color="var(--color-late)">지각</Pill> : null}
                    <span className="grow" />
                    <span className="muted small nowrap">{p.closedBy ? `확인 ${p.closedBy}` : `등록 ${p.createdBy}`}</span>
                  </span>
                  {passed.length ? <span className="muted small">통과: {passed.map((i) => i.title).join(", ")}</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );

  return (
    <article className="detail">
      <header className="detail-h">
        <div className="detail-title">
          <StudentName st={st} />
          <span className="grow" />
          <button type="button" className="btn" onClick={() => openSheet({ kind: "student", id: st.id })}>정보 수정</button>
          <a className="btn btn-primary" href={`#/register?student=${encodeURIComponent(st.id)}`}><Icon name="plus" size={18} /> 개별일정</a>
        </div>
        <div className="chips">
          {[...st.slots].sort((a, b) => a.dow - b.dow || a.time.localeCompare(b.time)).map((s, i) => <Pill key={i} color="var(--color-accent)">{DOW[s.dow - 1]} {s.time}</Pill>)}
          {st.slots.length === 0 ? <span className="muted small">정규 시간 없음</span> : null}
        </div>
        {st.memo ? <p className="muted">{st.memo}</p> : null}
        <p className="muted small">연락처: (시험판 — 비움)</p>
      </header>
      <div className="stats stats-4">
        <StatTile n={open.length} label="남은 클리닉" color="var(--color-accent)" />
        <StatTile n={ws.length} label="경고카드" color={ws.length >= th ? "var(--color-danger)" : undefined} />
        <StatTile n={closed.filter((s) => s.status === St.NOSHOW).length} label="노쇼" color="var(--color-status-noshow)" />
        <div className="stat"><span className="stat-n">{rate}</span><span className="stat-l">출석률</span></div>
      </div>
      {wide ? (
        <div className="cols cols-2">
          <div>{itemsSec}{upSec}</div>
          <div>{warnSec}{pastSec}</div>
        </div>
      ) : (<>{itemsSec}{upSec}{warnSec}{pastSec}</>)}
    </article>
  );
}
